/**
 * The control grid — one height ladder that every row-height control shares.
 *
 * **The defect this exists to close.** Measured on the rendered components, the five
 * controls that sit side by side in forms and toolbars had **four different heights**,
 * none of them declared:
 *
 * | control | before | why |
 * |---|---|---|
 * | `Button` | **32px** | the only one with an explicit height (`h-8`) |
 * | `DropdownButton` | **36px** | no height — `py-2` + a 20px line box |
 * | `TextField` | **38px** | no height — `py-2` + 20px line box + 2px border |
 * | `Dropdown` | **38px** | same |
 * | `Search` | **38px** | same |
 *
 * So a Button next to a TextField was **6px shorter, in every form footer in the
 * product**, and two *buttons* from the same design system disagreed with each other by
 * 4px. Not one of those numbers was a decision: 38 is `py-2` plus a line-height plus a
 * border, which is arithmetic, and it drifted the moment any of the three changed.
 *
 * A height that falls out of padding cannot be aligned against anything. So the height is
 * now stated, here, once, and every control imports it.
 *
 * **Why 28 / 36 / 44.**
 *
 * - All three are multiples of 4, so controls line up on the same grid as the spacing.
 * - `md` = **36** lands 2px from the old fields and 4px from the old Button — both move
 *   slightly and meet in the middle, rather than one being dragged to the other. It is
 *   also the modern default (shadcn 36, Polaris 36) and sits comfortably in a dense data
 *   product without the chunkiness of 40+.
 * - `sm` = **28** is for dense contexts: table-row actions, toolbars, inline filters. Above
 *   the 24px minimum target of WCAG SC 2.5.8.
 * - `lg` = **44** matches the comfortable touch target the iOS HIG asks for, so a primary
 *   action does not need a bespoke class to be thumb-friendly.
 *
 * **Type size is NOT part of this.** Every size renders text at its component's own token.
 * That rule already existed in `Search`, whose sizes once changed the font size too —
 * 14 / 16 / 18px — so the default Search rendered its text at 16px while every other
 * control rendered at 14. *Size is density; the type token is fixed.*
 */

import { ControlSize, LegacyControlSize } from '../../types/DesignSystem/controlSize';

/* Re-exported so a component can take the maps and the type from one import. */
export type { ControlSize, LegacyControlSize };

const LEGACY: Record<LegacyControlSize, ControlSize> = {
  small: 'sm',
  medium: 'md',
  large: 'lg',
};

/**
 * Accepts either dialect and returns the canonical one.
 *
 * Takes a **required** parameter deliberately. A default here (`size = 'md'`) would fire
 * whenever a caller passed `undefined`, which is exactly the trap that hid a real bug in
 * `ErrorState`'s `role` and, before that, in a test helper's `channelKey`. Each component
 * applies its own default at the destructure, where it is visible.
 */
export const resolveControlSize = (
  size: ControlSize | LegacyControlSize,
): ControlSize => (size in LEGACY ? LEGACY[size as LegacyControlSize] : (size as ControlSize));

/** Declared heights, in px — the numbers the classes below must agree with. */
export const CONTROL_PX: Record<ControlSize, number> = { sm: 28, md: 36, lg: 44 };

/** The height class. `h-7` = 28, `h-9` = 36, `h-11` = 44. */
export const CONTROL_HEIGHT: Record<ControlSize, string> = {
  sm: 'tw-h-7',
  md: 'tw-h-9',
  lg: 'tw-h-11',
};

/**
 * Horizontal padding for a **field** — a text input, a select trigger, a search row.
 *
 * Tighter than a button's on purpose: a field's value is *scanned*, and text that starts
 * further from the border is slower to find in a column of fields. `md` stays at the
 * `px-3` fields already used, so no field's text moves horizontally.
 */
export const FIELD_INSET: Record<ControlSize, string> = {
  sm: 'tw-px-2.5',
  md: 'tw-px-3',
  lg: 'tw-px-4',
};

/**
 * Horizontal padding for a **button** — wider than a field's, because a label needs air
 * either side of it to read as a target rather than as text. `md` stays at the `px-4`
 * buttons already used.
 */
export const BUTTON_INSET: Record<ControlSize, string> = {
  sm: 'tw-px-3',
  md: 'tw-px-4',
  lg: 'tw-px-5',
};

/** Gap between a control's icon and its label. Scales with the control, unlike the type. */
export const CONTROL_GAP: Record<ControlSize, string> = {
  sm: 'tw-gap-1',
  md: 'tw-gap-1.5',
  lg: 'tw-gap-2',
};
